// Reusable country / state dropdowns that populate hidden `countryAuto` /
// `stateAuto` form fields. The visible <select>s (rendered by
// templates/forms/include/country-auto.html and state-auto.html) are UI only and
// carry no name; the hidden fields they point at (via data-target) are what
// submit with the form. The state dropdown is only shown for AU / NZ and its
// options are filtered to the selected country. Mirrors the register page's
// dependent-dropdown behaviour, but parameterised to any form.

// AU / NZ state codes — must match the values in the state InputType (id 91).
const AUS_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];
const NZ_STATES = [
	'AUK', 'BP', 'CA', 'GI', 'HB', 'MAL', 'MW', 'NEL', 'NOR',
	'OTA', 'SOU', 'TAR', 'TAS', 'WAI', 'WC', 'WEL'];
// AU and NZ share the code TAS but label it differently.
const TAS_LABEL = { AU: 'Tasmania', NZ: 'Tasman' };

class FormCountryState {
	static #initialized = false;

	static initialize() {
		if (this.#initialized) return;
		this.#initialized = true;

		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.#setup());
		} else {
			this.#setup();
		}
	}

	static #setup() {
		document.querySelectorAll('.js-country-auto-select').forEach(countrySelect => {
			try {
				new FormCountryState(countrySelect).#bind();
			} catch (error) {
				console.warn('FormCountryState error:', error);
			}
		});
	}

	#countrySelect;
	#countryHidden;
	#stateSelect;
	#stateBlock;
	#stateHidden;
	#stateOptions;

	constructor(countrySelect) {
		this.#countrySelect = countrySelect;
		this.#countryHidden = document.getElementById(countrySelect.dataset.target);

		const form = countrySelect.closest('form');
		this.#stateSelect = form ? form.querySelector('.js-state-auto-select') : null;
		this.#stateBlock = form ? form.querySelector('.js-state-auto-block') : null;
		this.#stateHidden = this.#stateSelect
			? document.getElementById(this.#stateSelect.dataset.target)
			: null;

		// Snapshot the full state option list before we start filtering it.
		this.#stateOptions = this.#stateSelect
			? Array.from(this.#stateSelect.options)
				.filter(option => option.value !== '')
				.map(option => ({ value: option.value, text: option.text }))
			: [];
	}

	#bind() {
		this.#sortCountries();
		this.#hideState();

		this.#countrySelect.addEventListener('change', () => {
			const country = this.#countrySelect.value;
			if (this.#countryHidden) this.#countryHidden.value = country;

			if ((country === 'AU' || country === 'NZ') && this.#stateSelect) {
				this.#showState(country);
			} else {
				this.#hideState();
			}
		});

		if (this.#stateSelect) {
			this.#stateSelect.addEventListener('change', () => {
				if (this.#stateHidden) this.#stateHidden.value = this.#stateSelect.value;
			});
		}
	}

	// Float AU then NZ to the top, alphabetise the rest, and add a divider after them.
	#sortCountries() {
		const select = this.#countrySelect;
		const selected = select.value;
		const options = Array.from(select.options).filter(option => option.value !== '');

		options.sort((a, b) => {
			if (a.value === 'AU') return -1;
			if (b.value === 'AU') return 1;
			if (a.value === 'NZ') return -1;
			if (b.value === 'NZ') return 1;
			return a.text > b.text ? 1 : -1;
		});

		select.innerHTML = '';
		select.add(new Option('Select', ''));
		options.forEach((option, index) => {
			// Once AU and NZ are placed, separate them from the rest of the list.
			if (index === 2) {
				const divider = new Option('--------------------', '');
				divider.disabled = true;
				select.add(divider);
			}
			select.add(new Option(option.text, option.value));
		});
		select.value = selected;
	}

	#showState(country) {
		const codes = country === 'AU' ? AUS_STATES : NZ_STATES;
		const list = this.#stateOptions.filter(option => codes.includes(option.value));

		this.#stateSelect.innerHTML = '';
		this.#stateSelect.add(new Option('Select', ''));
		list.forEach(option => {
			const text = option.value === 'TAS' ? TAS_LABEL[country] : option.text;
			this.#stateSelect.add(new Option(text, option.value));
		});

		if (this.#stateBlock) this.#stateBlock.style.display = '';
		// Only require state while it is actually shown, so a hidden required
		// control can never silently block submission.
		this.#stateSelect.required = true;
		this.#stateSelect.value = '';
		if (this.#stateHidden) this.#stateHidden.value = '';
	}

	#hideState() {
		if (this.#stateBlock) this.#stateBlock.style.display = 'none';
		if (this.#stateSelect) {
			this.#stateSelect.required = false;
			this.#stateSelect.value = '';
		}
		if (this.#stateHidden) this.#stateHidden.value = '';
	}
}

FormCountryState.initialize();

export default FormCountryState;
